import {
  Model3DCapabilities,
  ModelDimensions,
  ModelGuidanceRanges,
  ModelStepsRanges,
  SkyboxStyleCategories,
} from './scenarioConstants';

import type { ScenarioImageModel, ScenarioModel3D, ScenarioModelInput } from './scenarioTypes';

/** Return type for image model capabilities */
export interface ImageModelCapabilities {
  isFlux: boolean;
  isFluxPro: boolean;
  isFluxPro1_1: boolean;
  isFluxPro1_1Ultra: boolean;
  isGptImage: boolean;
  isSdxl: boolean;
  supportedDimensions: ReadonlyArray<{ readonly width: number; readonly height: number; readonly label: string }>;
  defaultDimensions: { width: number; height: number };
  guidanceRange: { min: number; max: number; default: number };
  stepsRange: { min: number; max: number; default: number };
  supportsNegativePrompt: boolean;
  supportsScheduler: boolean;
  requiresPrompt: boolean;
  showSteps: boolean;
  showGuidance: boolean;
  showNumSamples: boolean;
  showSeed: boolean;
  showDimensions: boolean;
  /** Set of parameter names supported by this model (from model.inputs) */
  supportedParams: Set<string>;
  /** Whether model inputs have been loaded */
  hasInputs: boolean;
}

/** Model capabilities detection and configuration service */
export class ModelCapabilitiesService {
  /**
   * Get skybox style options organized by categories
   */
  getSkyboxStyles(): typeof SkyboxStyleCategories {
    return SkyboxStyleCategories;
  }

  /**
   * Detect model type and get comprehensive capabilities.
   * Uses model.inputs when available to determine supported parameters.
   */
  getImageModelCapabilities(model: ScenarioImageModel | null | undefined): ImageModelCapabilities {
    // Handle null/undefined model gracefully
    if (!model) {
      return this.getDefaultCapabilities();
    }

    const modelName = model.name?.toLowerCase() ?? '';
    const modelId = model.id?.toLowerCase() ?? '';
    const tags = model.tags ?? [];
    const inputs: ScenarioModelInput[] = model.inputs ?? [];

    // Build supported params set from model inputs
    const supportedParams = new Set(inputs.map(i => i.name));
    const hasInputs = supportedParams.size > 0;

    // Model type detection based on Scenario's exact patterns
    const isFlux =
      modelName.includes('flux') || modelId.includes('flux') || tags.some((tag: string) => tag.includes('flux'));
    const isFluxPro = isFlux && (modelName.includes('pro') || modelId.includes('pro'));
    const isFluxPro1_1 =
      isFluxPro && (modelName.includes('1.1') || modelId.includes('1.1') || modelId.includes('1_1'));
    const isFluxPro1_1Ultra =
      modelId === 'flux.1.1-pro-ultra' ||
      modelId.includes('flux.1.1-pro-ultra') ||
      modelId.includes('flux-1.1-pro-ultra') ||
      (isFluxPro1_1 && (modelName.includes('ultra') || modelId.includes('ultra')));
    const isGptImage = modelName.includes('gpt') && modelName.includes('image');
    const isSdxl =
      modelName.includes('sdxl') ||
      modelName.includes('stable-diffusion-xl') ||
      tags.some((tag: string) => tag.includes('sdxl'));

    // Determine supported dimensions based on model type
    let supportedDimensions;

    if (isFluxPro1_1Ultra) {
      supportedDimensions = ModelDimensions.fluxProUltra;
    } else if (isGptImage) {
      supportedDimensions = ModelDimensions.gptImage;
    } else if (modelName.includes('sd-1_5') || modelId.includes('sd-1_5')) {
      supportedDimensions = ModelDimensions.sd15;
    } else {
      supportedDimensions = ModelDimensions.standard;
    }

    // Find the 1:1 (square) ratio default from available dimensions
    const squareDimension = supportedDimensions.find(d => d.width === d.height) ?? supportedDimensions[0];
    const defaultDimensions = { width: squareDimension.width, height: squareDimension.height };

    // Determine control visibility based on model inputs when available, otherwise fall back to name-based detection
    // Determine control visibility based on model inputs when available
    // Handle multiple param name variations used by different models
    const showSteps = hasInputs
      ? (supportedParams.has('numInferenceSteps') || supportedParams.has('steps'))
      : !isFluxPro1_1;
    const showGuidance = hasInputs
      ? (supportedParams.has('guidance') || supportedParams.has('guidanceScale'))
      : !isFluxPro1_1;
    const showNumSamples = hasInputs
      ? (supportedParams.has('numSamples') || supportedParams.has('numOutputs'))
      : true; // Default to showing if no inputs
    const showSeed = hasInputs
      ? supportedParams.has('seed')
      : true; // Default to showing if no inputs
    const showDimensions = hasInputs
      ? (supportedParams.has('width') || supportedParams.has('height') || supportedParams.has('aspectRatio'))
      : true; // Default to showing if no inputs

    // Check for negativePrompt support from model inputs, fall back to name-based detection
    const supportsNegativePrompt = hasInputs
      ? supportedParams.has('negativePrompt')
      : !isFlux; // FLUX models typically don't use negative prompts

    // Check for scheduler support from model inputs
    const supportsScheduler = hasInputs
      ? supportedParams.has('scheduler')
      : !isFlux && !isGptImage; // Default to false for FLUX and GPT-Image models

    return {
      isFlux,
      isFluxPro,
      isFluxPro1_1,
      isFluxPro1_1Ultra,
      isGptImage,
      isSdxl,
      supportedDimensions,
      defaultDimensions,
      guidanceRange: isFluxPro1_1 ? ModelGuidanceRanges.fluxPro11 : ModelGuidanceRanges.standard,
      stepsRange: isFluxPro1_1 ? ModelStepsRanges.fluxPro11 : ModelStepsRanges.standard,
      supportsNegativePrompt,
      supportsScheduler,
      requiresPrompt: isFluxPro || isFluxPro1_1 || isGptImage,
      showSteps,
      showGuidance,
      showNumSamples,
      showSeed,
      showDimensions,
      supportedParams,
      hasInputs,
    };
  }

  /** Get default capabilities when no model is selected */
  private getDefaultCapabilities(): ImageModelCapabilities {
    return {
      isFlux: false,
      isFluxPro: false,
      isFluxPro1_1: false,
      isFluxPro1_1Ultra: false,
      isGptImage: false,
      isSdxl: false,
      supportedDimensions: ModelDimensions.standard,
      defaultDimensions: { width: 1024, height: 1024 },
      guidanceRange: ModelGuidanceRanges.standard,
      stepsRange: ModelStepsRanges.standard,
      supportsNegativePrompt: true,
      supportsScheduler: true,
      requiresPrompt: false,
      showSteps: true,
      showGuidance: true,
      showNumSamples: true,
      showSeed: true,
      showDimensions: true,
      supportedParams: new Set(),
      hasInputs: false,
    };
  }

  /**
   * Get 3D model capabilities based on model type
   */
  getModel3DCapabilities(model: ScenarioModel3D): {
    supportsMultiView: boolean;
    maxFaces: number;
    stepRange: { min: number; max: number; default: number };
    guidanceRange: { min: number; max: number; default: number };
  } {
    const modelName = model.name?.toLowerCase() ?? '';
    const modelId = model.id?.toLowerCase() ?? '';

    // Model-specific capabilities based on known models
    const isHunyuan = modelName.includes('hunyuan') || modelId.includes('hunyuan');
    const isDirect3D = modelName.includes('direct3d') || modelId.includes('direct3d');

    if (isHunyuan) return Model3DCapabilities.hunyuan;
    if (isDirect3D) return Model3DCapabilities.direct3d;
    return Model3DCapabilities.standard;
  }

  /** @deprecated Use getModel3DCapabilities instead */
  get3DModelCapabilities(model: ScenarioModel3D) { return this.getModel3DCapabilities(model); }

  /**
   * Get supported dimensions for a model
   */
  getSupportedDimensions(
    model: ScenarioImageModel | any
  ): ReadonlyArray<{ readonly width: number; readonly height: number; readonly label: string }> {
    const capabilities = this.getImageModelCapabilities(model);
    return capabilities.supportedDimensions;
  }

  /**
   * Get default dimensions for a model
   */
  getDefaultDimensions(model: ScenarioImageModel | any): { width: number; height: number } {
    const capabilities = this.getImageModelCapabilities(model);
    return capabilities.defaultDimensions;
  }

  /**
   * Get guidance range for a model
   */
  getGuidanceRange(model: ScenarioImageModel | any): { min: number; max: number; default: number } {
    const capabilities = this.getImageModelCapabilities(model);
    return capabilities.guidanceRange;
  }

  /**
   * Get steps range for a model
   */
  getStepsRange(model: ScenarioImageModel | any): { min: number; max: number; default: number } {
    const capabilities = this.getImageModelCapabilities(model);
    return capabilities.stepsRange;
  }
}

/** Singleton instance */
export const modelCapabilities = new ModelCapabilitiesService();

